import processEnv, { Environment } from "../app/Environment";
import fetchService from "../utils/FetchService";

export interface S3DataFile {
  key: string;
  signedUrl: string;
}

export interface DataExportJobResponse {
  jobId: string;
  status?: "PENDING";
}

export type DataExportStatus =
  | {
      jobId: string;
      status: "PENDING" | "RUNNING";
    }
  | {
      jobId: string;
      status: "COMPLETED";
      key: string;
      signedUrl: string;
    }
  | {
      jobId: string;
      status: "FAILED";
      error: string;
    };

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function asStringField(
  value: Record<string, unknown>,
  field: string,
  context: string
): string {
  const content = value[field];
  if (typeof content !== "string" || content.length === 0) {
    throw new Error(`Malformed ${context} response: invalid ${field}`);
  }
  return content;
}

export class DataService {

  constructor(private env: Environment = processEnv) { }

  async initiateExport(): Promise<DataExportJobResponse> {
    const response = await fetchService.fetch(`${this.env.endpoint}/data`, {
      method: "POST",
    });
    if (response.status !== 202 && response.status !== 200) {
      const text = await response.text();
      throw new Error(text || `Failed to initiate export (${response.status})`);
    }
    const json = (await response.json()) as unknown;
    if (!isObject(json)) {
      throw new Error("Malformed initiate export response");
    }
    return {
      jobId: asStringField(json, "jobId", "initiate export"),
      status: json.status === "PENDING" ? "PENDING" : undefined,
    };
  }

  async getExportStatus(jobId: string): Promise<DataExportStatus> {
    const response = await fetchService.fetch(
      `${this.env.endpoint}/data/export/${encodeURIComponent(jobId)}`,
      { method: "GET" }
    );
    if (response.status !== 200) {
      const text = await response.text();
      throw new Error(text || `Failed to get export status (${response.status})`);
    }
    const json = (await response.json()) as unknown;
    if (!isObject(json)) {
      throw new Error("Malformed export status response");
    }
    const normalizedJobId = asStringField(json, "jobId", "export status");
    const status = asStringField(json, "status", "export status");
    if (status === "PENDING" || status === "RUNNING") {
      return { jobId: normalizedJobId, status };
    }
    if (status === "COMPLETED") {
      return {
        jobId: normalizedJobId,
        status: "COMPLETED",
        key: asStringField(json, "key", "export status"),
        signedUrl: asStringField(json, "signedUrl", "export status"),
      };
    }
    if (status === "FAILED") {
      return {
        jobId: normalizedJobId,
        status: "FAILED",
        error: asStringField(json, "error", "export status"),
      };
    }
    throw new Error("Malformed export status response: invalid status");
  }

  // Legacy method - kept for backward compatibility
  async getDataFile(): Promise<S3DataFile> {
    return this.initiateExport().then((_response) => {
      // This will now return a jobId instead of the file
      // The actual file will come via WebSocket
      throw new Error("Use initiateExport() and listen to WebSocket for completion");
    });
  }
}

const data = new DataService();
export default data;
